import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const PRIMARY_REASONS = [
  { id: 'routine',     label: "The app didn't fit into my routine" },
  { id: 'results',     label: "I didn't see results or progress" },
  { id: 'price',       label: "It was too expensive for what I got" },
  { id: 'bugs',        label: "I had technical issues or bugs" },
  { id: 'alternative', label: "I found a better alternative" },
  { id: 'features',    label: "The app didn't have enough features" },
  { id: 'break',       label: "I just needed a break — I might come back" },
];

const FOLLOWUP = {
  routine:     { q: "What time of day were you most likely to use the app?",  p: "e.g. mornings, evenings, weekends..." },
  results:     { q: "Which part of the app did you use most?",                p: "e.g. routines, food guide, community..." },
  price:       { q: "What price would feel fair to you?",                     p: "e.g. $5/month, one-time purchase..." },
  bugs:        { q: "Can you describe what went wrong?",                      p: "Tell us what happened..." },
  alternative: { q: "What does that app do better?",                          p: "We'd genuinely love to know..." },
  features:    { q: "What features were you missing most?",                   p: "e.g. tracking, reminders, more conditions..." },
  break:       { q: "What would bring you back to Cura?",                    p: "What would make it worth returning?" },
};

const STEPS = { REASON: 0, FOLLOWUP: 1, OPEN: 2, DONE: 3 };

export default function FeedbackForm() {
  const uid = new URLSearchParams(window.location.search).get('uid') || '';
  const [step, setStep] = useState(STEPS.REASON);
  const [reason, setReason] = useState('');
  const [followupAnswer, setFollowupAnswer] = useState('');
  const [openFeedback, setOpenFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'reviews'), {
        userId: uid,
        source: 'web_form',
        reason,
        followupQuestion: FOLLOWUP[reason]?.q || '',
        followupAnswer,
        openFeedback,
        timestamp: serverTimestamp(),
      });
      setStep(STEPS.DONE);
    } catch (e) {
      alert('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={s.page}>
      {/* Background decorative blobs */}
      <div style={s.blob1} />
      <div style={s.blob2} />

      <div style={s.container}>
        {/* Logo */}
        <div style={s.logoWrap}>
          <img src="/logo.png" alt="Cura" style={s.logo} />
        </div>

        <div style={s.card}>
          {/* Progress bar */}
          {step < STEPS.DONE && (
            <div style={s.progressBar}>
              <div style={{ ...s.progressFill, width: `${((step + 1) / 3) * 100}%` }} />
            </div>
          )}

          {step === STEPS.REASON && (
            <ReasonStep reason={reason} setReason={setReason} onNext={() => setStep(STEPS.FOLLOWUP)} />
          )}
          {step === STEPS.FOLLOWUP && (
            <FollowupStep
              fu={FOLLOWUP[reason]}
              value={followupAnswer}
              onChange={setFollowupAnswer}
              onBack={() => setStep(STEPS.REASON)}
              onNext={() => setStep(STEPS.OPEN)}
            />
          )}
          {step === STEPS.OPEN && (
            <OpenStep
              value={openFeedback}
              onChange={setOpenFeedback}
              onBack={() => setStep(STEPS.FOLLOWUP)}
              onSubmit={submit}
              submitting={submitting}
            />
          )}
          {step === STEPS.DONE && <DoneStep />}
        </div>

        <p style={s.footer}>thecura.app · Your feedback is private and used only to improve Cura</p>
      </div>
    </div>
  );
}

function ReasonStep({ reason, setReason, onNext }) {
  return (
    <div>
      <div style={s.stepHeader}>
        <div style={s.stepTag}>Feedback</div>
        <h1 style={s.title}>We're sorry to see you go</h1>
        <p style={s.sub}>What was the main reason you cancelled? Your answer helps us build something you'll actually love.</p>
      </div>
      <div style={s.options}>
        {PRIMARY_REASONS.map(r => (
          <button
            key={r.id}
            style={{ ...s.option, ...(reason === r.id ? s.optionActive : {}) }}
            onClick={() => setReason(r.id)}
          >
            <div style={{ ...s.radio, ...(reason === r.id ? s.radioActive : {}) }}>
              {reason === r.id && <div style={s.radioDot} />}
            </div>
            <span style={s.optionLabel}>{r.label}</span>
          </button>
        ))}
      </div>
      <PrimaryBtn label="Continue" disabled={!reason} onClick={onNext} />
    </div>
  );
}

function FollowupStep({ fu, value, onChange, onBack, onNext }) {
  return (
    <div>
      <BackBtn onClick={onBack} />
      <div style={s.stepHeader}>
        <div style={s.stepTag}>One more thing</div>
        <h1 style={s.title}>{fu.q}</h1>
        <p style={s.sub}>This helps us understand exactly what to improve.</p>
      </div>
      <FeedbackArea value={value} onChange={onChange} placeholder={fu.p} rows={4} />
      <PrimaryBtn label="Continue" onClick={onNext} />
    </div>
  );
}

function OpenStep({ value, onChange, onBack, onSubmit, submitting }) {
  return (
    <div>
      <BackBtn onClick={onBack} />
      <div style={s.stepHeader}>
        <div style={s.stepTag}>Almost done</div>
        <h1 style={s.title}>Anything else you want us to know?</h1>
        <p style={s.sub}>Optional — but we read every single response.</p>
      </div>
      <FeedbackArea
        value={value}
        onChange={onChange}
        placeholder="Share any thoughts, features you wished existed, or things that frustrated you..."
        rows={5}
      />
      <PrimaryBtn label={submitting ? 'Submitting...' : 'Submit Feedback'} disabled={submitting} onClick={onSubmit} />
      <button style={s.skipBtn} onClick={onSubmit} disabled={submitting}>Skip and submit</button>
    </div>
  );
}

function DoneStep() {
  return (
    <div style={s.doneWrap}>
      <div style={s.doneIconWrap}>
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M6 16L13 23L26 9" stroke="#24554C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <h1 style={{ ...s.title, textAlign: 'center' }}>Thank you</h1>
      <p style={{ ...s.sub, textAlign: 'center', maxWidth: 320, margin: '0 auto' }}>
        Your feedback means a lot to us. We'll use it to make Cura better for everyone.
      </p>
      <div style={s.doneFooter}>
        <p style={{ ...s.sub, textAlign: 'center', marginTop: 24 }}>If you ever want to come back, we'll be here. 🌿</p>
      </div>
    </div>
  );
}

function PrimaryBtn({ label, onClick, disabled }) {
  return (
    <button style={{ ...s.primaryBtn, ...(disabled ? s.primaryBtnDisabled : {}) }} onClick={onClick} disabled={disabled}>
      {label}
    </button>
  );
}

function BackBtn({ onClick }) {
  return (
    <button style={s.backBtn} onClick={onClick}>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: 6 }}>
        <path d="M9 2L4 7L9 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      Back
    </button>
  );
}

function FeedbackArea({ value, onChange, placeholder, rows }) {
  return (
    <textarea
      style={s.textarea}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      maxLength={1500}
    />
  );
}

const s = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px 16px',
    position: 'relative',
    overflow: 'hidden',
  },
  blob1: {
    position: 'fixed', top: '-10%', right: '-5%',
    width: 400, height: 400, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(160,230,160,0.25) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  blob2: {
    position: 'fixed', bottom: '-10%', left: '-5%',
    width: 350, height: 350, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(36,85,76,0.08) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  container: {
    width: '100%', maxWidth: 520,
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 20, position: 'relative', zIndex: 1,
  },
  logoWrap: { marginBottom: 4 },
  logo: { height: 36, objectFit: 'contain' },
  card: {
    width: '100%',
    background: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid rgba(255,255,255,0.9)',
    boxShadow: '0 20px 60px rgba(36,85,76,0.12), 0 1px 0 rgba(255,255,255,0.8) inset',
    padding: '32px 32px 36px',
    overflow: 'hidden',
  },
  progressBar: {
    width: '100%', height: 3,
    background: 'rgba(36,85,76,0.08)',
    borderRadius: 99,
    marginBottom: 28,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #24554c, #6db8a6)',
    borderRadius: 99,
    transition: 'width 0.4s cubic-bezier(0.4,0,0.2,1)',
  },
  stepHeader: { marginBottom: 24 },
  stepTag: {
    display: 'inline-block',
    fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
    color: 'var(--green-700)', background: 'var(--green-100)',
    padding: '4px 10px', borderRadius: 99,
    marginBottom: 12, textTransform: 'uppercase',
  },
  title: {
    fontSize: 22, fontWeight: 800, color: 'var(--text-primary)',
    lineHeight: 1.3, marginBottom: 8, letterSpacing: '-0.3px',
  },
  sub: { fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.65 },
  options: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 },
  option: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '13px 16px',
    background: 'rgba(245,252,240,0.8)',
    border: '1.5px solid rgba(36,85,76,0.1)',
    borderRadius: 'var(--radius-sm)',
    textAlign: 'left', transition: 'all 0.15s ease',
    cursor: 'pointer',
  },
  optionActive: {
    background: 'rgba(229,246,215,0.9)',
    borderColor: 'var(--green-700)',
    boxShadow: '0 0 0 3px rgba(36,85,76,0.06)',
  },
  radio: {
    width: 18, height: 18, borderRadius: '50%',
    border: '1.5px solid rgba(36,85,76,0.25)',
    flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.15s',
  },
  radioActive: { border: '1.5px solid var(--green-800)', background: 'var(--green-800)' },
  radioDot: { width: 7, height: 7, borderRadius: '50%', background: 'white' },
  optionLabel: { fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', lineHeight: 1.4 },
  textarea: {
    width: '100%', padding: '14px 16px',
    background: 'rgba(245,252,240,0.7)',
    border: '1.5px solid rgba(36,85,76,0.12)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.65,
    resize: 'vertical', marginBottom: 20,
    transition: 'border-color 0.15s',
  },
  primaryBtn: {
    width: '100%', padding: '15px',
    background: 'linear-gradient(135deg, #1a3d35 0%, #24554c 50%, #3a8575 100%)',
    color: 'white', borderRadius: 'var(--radius-sm)',
    fontSize: 15, fontWeight: 700, letterSpacing: '0.01em',
    boxShadow: '0 4px 20px rgba(36,85,76,0.3)',
    transition: 'opacity 0.15s, transform 0.15s',
  },
  primaryBtnDisabled: { opacity: 0.35, boxShadow: 'none' },
  skipBtn: {
    width: '100%', marginTop: 10, padding: '10px',
    background: 'transparent', color: 'var(--text-muted)',
    fontSize: 13, fontWeight: 600,
  },
  backBtn: {
    display: 'flex', alignItems: 'center',
    background: 'none', color: 'var(--text-muted)',
    fontSize: 13, fontWeight: 600, marginBottom: 20, padding: 0,
  },
  doneWrap: { padding: '16px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 },
  doneIconWrap: {
    width: 72, height: 72, borderRadius: '50%',
    background: 'linear-gradient(135deg, var(--green-100), var(--green-200))',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 8px 24px rgba(36,85,76,0.15)',
    marginBottom: 8,
  },
  doneFooter: { marginTop: 8 },
  footer: {
    fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.6,
  },
};
